import React from 'react';
import PropTypes from 'prop-types';
import {formatCurrency, parseCurrency} from "utils/i18n";
import ensureKeyed from '../../utils/ensureKeyed';
import {contracts} from '../../ducks/index';
import registeredComponent from "utils/registeredComponent";
import duckify, {singleDuck, currentCustomer} from "utils/duckify";
import Placeholder from '../Placeholder';
import classNames from 'classnames';
import SplitContractSelector from '../contracts/SplitContractSelector';
import FeeListItemAmount from './FeeListItemAmount';

const TotalsSummaryField = ({
  currentCustomer,
  contract,
  templateHasTuitionField,
  templateHasPaymentPlanField,
  customTotalText,
  customPaymentPlanText,
  customDepositText,
  duckMeta,
  allDucksReady,
  selectedBillingPayerId
}) => <Placeholder.formField ready={allDucksReady} content={() => {
  const amountPlaceholder = (amount) => <Placeholder.text ready={allDucksReady && !duckMeta.contract.writing} lines={1} width={160}>
    <span className={'amount'}>{formatCurrency(amount)}</span>
  </Placeholder.text>;

  const selectedDepositCharges = (contract.contract_charges || []).filter(charge =>
    charge.add_to_deposit && charge.selected
  );

  return (
    <React.Fragment>
      <div className={'sa-form-field-group sa-form-tuition-group-header-row'}>
        <div className={'sa-form-field-group-prompt'}>

        </div>
        <div className={'sa-form-field-group-body'}>
          <div className={'fee-list-item'}>
            <div className={'sa-form-tuition-item-name'}/>
            {
              contract.has_multiple_splits && (
                <div className={'fee-list-item-amount fee-list-item-amount-payer-split'}>
                  <SplitContractSelector contract_id={contract.id}/>
                </div>
              )
            }
            <div className={'fee-list-item-amount'}>
              Total
            </div>
          </div>
        </div>
      </div>
      <div className={'sa-form-field-group'}>
        <div className={'sa-form-field-group-prompt'}/>
        <div className={'sa-form-field-group-body sa-form-totals-summary'}>
          {(contract.tuition_and_fees_total != 0 || templateHasTuitionField) &&
            <div className={'sa-form-totals-summary-item sa-form-totals-summary-tuition-total'}>
              <div className={'sa-form-totals-summary-item-body'}>
                {customTotalText || 'Tuition & Fees Total'}
              </div>

              <FeeListItemAmount amountProperty={'tuition_and_fees_total'} contract_id={contract.id}/>
            </div>
          }

          {(contract.payment_plan_total != 0  && templateHasPaymentPlanField) &&
            <div className={'sa-form-totals-summary-item sa-form-totals-summary-payment-plan-total'}>
              <div className={'sa-form-totals-summary-item-body'}>
                {customPaymentPlanText || "Payment Plan Fees"}
              </div>

              <FeeListItemAmount amountProperty={'payment_plan_total'} contract_id={contract.id}/>
            </div>
          }

          <div className={'sa-form-totals-summary-item sa-form-totals-summary-before-deposit-total'}>
            <div className={'sa-form-totals-summary-item-body'}>
              Total
            </div>

            <FeeListItemAmount contract_id={contract.id}
                               getSplitAmount={({currentSplit}) => parseCurrency(currentSplit.tuition_and_fees_total) + parseCurrency(currentSplit.payment_plan_total)}
                               getTotalAmount={({contract}) => parseCurrency(contract.tuition_and_fees_total) + parseCurrency(contract.payment_plan_total)}
            />
          </div>


          {contract.deposit_exists &&
            <React.Fragment>
              <div className={'deposit-summary'}>
                {customDepositText || "The following fees are due with the signed contract:"}
              </div>
              <div className={'sa-form-totals-summary-item sa-form-totals-summary-deposit-due'}>
                <div className={'sa-form-totals-summary-item-body'} style={{fontWeight: 'normal'}}>
                  {currentCustomer.deposit_name || "Deposit"}
                </div>

                <FeeListItemAmount amountProperty={'deposit_charge'} contract_id={contract.id}/>
              </div>


              {
                selectedDepositCharges
                .map(charge =>
                  <div className={classNames(
                    'sa-form-totals-summary-item',
                    'sa-form-additional-fee',
                    charge.charge_type
                  )}>
                    <div className={'sa-form-totals-summary-item-body'} style={{fontWeight: 'normal'}}>
                      {charge.name}
                    </div>

                    <FeeListItemAmount contractFee={charge} contract_id={contract.id}/>
                  </div>
                )
                .map(ensureKeyed())
              }

              <div className={'sa-form-totals-summary-item'}>
                <div className={'sa-form-totals-summary-item-body total_payment'}>
                  Amount Due Now
                </div>

                <FeeListItemAmount amountProperty={'deposit_amount'} contract_id={contract.id} className={'total_payment'}/>
              </div>
            </React.Fragment>
          }
          <div className={'sa-form-totals-summary-item sa-form-totals-summary-after-deposit-total'}>
            <div className={'sa-form-totals-summary-item-body'}>
              Total After Payment
            </div>

            <FeeListItemAmount contract_id={contract.id}
                               getSplitAmount={({currentSplit}) => parseCurrency(currentSplit.tuition_and_fees_total) + parseCurrency(currentSplit.payment_plan_total) - parseCurrency(currentSplit.deposit_amount)}
                               getTotalAmount={({contract}) => parseCurrency(contract.tuition_and_fees_total) + parseCurrency(contract.payment_plan_total) - parseCurrency(contract.deposit_amount)}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}}/>;

TotalsSummaryField.propTypes = {
  contract_id: PropTypes.string,
  contract: PropTypes.object,
  templateHasTuitionField: PropTypes.bool,
  templateHasPaymentPlanField: PropTypes.bool,
  customTotalText: PropTypes.string,
  customPaymentPlanText: PropTypes.string,
  customDepositText: PropTypes.string,
  selectedBillingPayerId: PropTypes.string
};

const mapDucksToProps = ({contract_id}) => ({
  currentCustomer,
  contract: singleDuck(contracts, contract_id)
});

const mapStateToProps = (state, props) => ({
  selectedBillingPayerId: state.contracts.currentContract.selectedBillingPayerId
});

export default registeredComponent('TotalsSummaryField')(duckify(mapDucksToProps, mapStateToProps)(TotalsSummaryField));
